import { ApiClient } from "../api.client";
import {
  CreateAffiliateUserRequest,
  CreateAffiliateUserResponse,
  GetAffiliatePaymentPeriodsRequest,
  GetAffiliatePaymentPeriodsResponse,
  GetAffiliatePaymentSumRequest,
  GetAffiliatePaymentSumResponse,
  GetAffiliateUserRequest,
  GetAffiliateUserResponse,
  GetAffiliateUsersRequest,
  GetAffiliateUsersResponse,
  PayAffiliatePaymentRequest,
  PayAffiliatePaymentResponse,
  UpdateAffiliatePaymentSettingsRequest,
  UpdateAffiliatePaymentSettingsResponse,
  GetAffiliatePayoutsRequest,
  GetAffiliatePaymentsPerMonthRequest,
  GetAffiliatePaymentsPerMonthResponse,
  UpdateAffiliateStatusRequest,
  UpdateAffiliateStatusResponse,
  GetAffiliatePayoutsResponse,
  GetAffiliateEarningsRequest,
  GetAffiliateEarningsResponse,
  GetAcquiredUsersRequest,
  GetAcquiredUsersResponse,
  UpdateTrialOfferRequest,
  UpdateTrialOfferResponse,
} from "./types";

const AffiliateApi = {
  getAffiliateUsers(params?: GetAffiliateUsersRequest) {
    return ApiClient.get<GetAffiliateUsersResponse>("/admin/affiliates", {
      params,
    });
  },
  getAffiliateUser(params: GetAffiliateUserRequest) {
    return ApiClient.get<GetAffiliateUserResponse>(
      `/admin/affiliates/${params.id}`
    );
  },
  createAffiliateUser(body: CreateAffiliateUserRequest) {
    return ApiClient.post<
      CreateAffiliateUserRequest,
      CreateAffiliateUserResponse
    >(`/admin/affiliates/create`, body);
  },
  updateAffiliateStatus({
    affiliateId,
    ...body
  }: UpdateAffiliateStatusRequest) {
    return ApiClient.patch<
      Omit<UpdateAffiliateStatusRequest, "affiliateId">,
      UpdateAffiliateStatusResponse
    >(`/admin/affiliates/${affiliateId}/set-status`, body);
  },
  updateAffiliatePaymentSettings({
    affiliateId,
    ...body
  }: UpdateAffiliatePaymentSettingsRequest) {
    return ApiClient.patch<
      Omit<UpdateAffiliatePaymentSettingsRequest, "affiliateId">,
      UpdateAffiliatePaymentSettingsResponse
    >(`/admin/affiliates/${affiliateId}/update-payment-settings`, body);
  },
  getAffiliatePaymentPeriods({
    affiliateId,
  }: GetAffiliatePaymentPeriodsRequest) {
    return ApiClient.get<GetAffiliatePaymentPeriodsResponse>(
      `/admin/affiliates/${affiliateId}/payment/periods`
    );
  },
  getAffiliatePaymentSum({
    affiliateId,
    year,
    month,
  }: GetAffiliatePaymentSumRequest) {
    return ApiClient.get<GetAffiliatePaymentSumResponse>(
      `/admin/affiliates/${affiliateId}/payment/periods/unpaid-sum`,
      {
        params: {
          year,
          month,
        },
      }
    );
  },
  payAffiliatePayment({ affiliateId, ...body }: PayAffiliatePaymentRequest) {
    return ApiClient.post<
      Omit<PayAffiliatePaymentRequest, "affiliateId">,
      PayAffiliatePaymentResponse
    >(`/admin/affiliates/${affiliateId}/payment/periods/pay`, body);
  },
  downloadCsv(params?: GetAffiliateUsersRequest) {
    return ApiClient.get<Blob>(`/admin/affiliates/export/csv`, {
      params,
      headers: {
        Accept: "text/csv",
      },
      responseType: "blob",
    });
  },
  getAffiliatePayouts({ affiliateId, ...params }: GetAffiliatePayoutsRequest) {
    return ApiClient.get<GetAffiliatePayoutsResponse>(
      `/admin/affiliates/${affiliateId}/payouts`,
      {
        params: {
          ...params,
        },
      }
    );
  },
  getAffiliateEarnings({
    affiliateId,
    ...params
  }: GetAffiliateEarningsRequest) {
    return ApiClient.get<GetAffiliateEarningsResponse>(
      `/admin/affiliates/${affiliateId}/earnings`,
      {
        params,
      }
    );
  },
  getAffiliatePaymentsPerMonth({
    affiliateId,
    ...params
  }: GetAffiliatePaymentsPerMonthRequest) {
    return ApiClient.get<GetAffiliatePaymentsPerMonthResponse>(
      `/admin/affiliates/${affiliateId}/payouts/payments`,
      {
        params,
      }
    );
  },
  downloadXlsx(params?: GetAffiliateUsersRequest) {
    return ApiClient.get<Blob>(`/admin/affiliates/export/xlsx`, {
      params,
      headers: {
        Accept: "text/xlsx",
      },
      responseType: "blob",
    });
  },
  getAcquiredUsers({ id, ...params }: GetAcquiredUsersRequest) {
    return ApiClient.get<GetAcquiredUsersResponse>(
      `/admin/affiliates/${id}/acquired-users`,
      { params }
    );
  },
  updateTrialOffer({ affiliateId, ...body }: UpdateTrialOfferRequest) {
    return ApiClient.patch<
      Omit<UpdateTrialOfferRequest, "affiliateId">,
      UpdateTrialOfferResponse
    >(`/admin/affiliates/${affiliateId}/update-trial-offer`, body);
  },
};

export { AffiliateApi };
